<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, minLength, sameAs } from '@vuelidate/validators'
import DialogRevamp from '@/components/DialogRevamp.vue'
import InputFieldFloatingWithMessage from '@/components/ui/input/InputFieldFloatingWithMessage.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	errorApiMessage: {
		type: String,
		default: '',
	},
})
const emit = defineEmits(['hide', 'backStep', 'submit'])
const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const textContent: any = injectStrict('textContent')

const isLoading = ref(false)
const formState = reactive({
	password: '',
	confirmPassword: '',
})
const rules = {
	password: {
		required,
		minLength: minLength(6),
	},
	confirmPassword: {
		required,
		minLength: minLength(6),
		sameAsPassword: sameAs(computed(() => formState.password)),
	},
}
const $v = useVuelidate(rules, formState)

const passwordErrorMessage = computed(() => {
	if ($v.value.password.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.password.minLength.$invalid) {
		return textContent.input_error_password_min_length
	}
	return ''
})

const passwordConfirmErrorMessage = computed(() => {
	if ($v.value.confirmPassword.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.confirmPassword.minLength.$invalid) {
		return textContent.input_error_password_min_length
	}
	if ($v.value.confirmPassword.sameAsPassword.$invalid) {
		return textContent.input_error_password_same
	}
	return ''
})
const isErrorApiMessageExist = computed(() => props.errorApiMessage !== '')

function backStep() {
	emit('backStep')
}

function submit() {
	$v.value.$touch()
	if ($v.value.$invalid) {
		return
	}
	isLoading.value = true
	emit('submit', {
		password: formState.password,
	})
}

function hideDialogDirectly() {
	emit('hide')
}

watch(
	() => isErrorApiMessageExist.value,
	value => {
		if (value) {
			isLoading.value = false
		}
	},
)
</script>

<template>
	<DialogRevamp
		class="bs-auth-reset"
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isCustomOverlayFunction="true"
		:isShowDefaultClose="false"
		:size="'sm'"
		:isSecondLayer="true"
		@overlayClick="backStep"
	>
		<template #header>
			<div class="bs-auth-back-header">
				<button class="bs-icon bs-auth-back-header__back" @click="backStep">
					<BaseIcon :name="'back'" class="h-16px w-16px fill-n-950" />
				</button>
				<h5 class="bs-auth__title">
					{{ textContent.reset_password_title }}
				</h5>
			</div>
		</template>
		<template #content>
			<div class="bs-auth-reset__content">
				<BaseAttentionBox
					v-if="isErrorApiMessageExist"
					class="bs-auth__attention-box"
					:icon="'error'"
					:background="'error'"
				>
					<template #description>
						{{ errorApiMessage }}
					</template>
				</BaseAttentionBox>
				<p class="bs-auth-reset__content__description">
					{{ textContent.personal_details_description }}
				</p>
				<InputFieldFloatingWithMessage
					class="bs-auth-reset__content__password"
					v-model="$v.password.$model"
					type="password"
					:disabled="isLoading"
					:label="textContent.input_label_password_reset"
					:isError="$v.password.$error"
					:errorMessage="passwordErrorMessage"
				></InputFieldFloatingWithMessage>
				<InputFieldFloatingWithMessage
					class="bs-auth-reset__content__confirm-password"
					v-model="$v.confirmPassword.$model"
					type="password"
					:disabled="isLoading"
					:label="textContent.input_label_password_confirm_reset"
					:isError="$v.confirmPassword.$error"
					:errorMessage="passwordConfirmErrorMessage"
				></InputFieldFloatingWithMessage>
			</div>
		</template>
		<template #footer>
			<BaseButton
				:size="'large'"
				:isFull="true"
				:isLoading="isLoading"
				@click="submit"
			>
				{{ textContent.button_confirm }}
			</BaseButton>
		</template>
	</DialogRevamp>
</template>
